import React, { useState, useRef, useEffect } from 'react';
import * as d3 from "d3";
import cdf from '@stdlib/stats-base-dists-normal-cdf';

function poisson(k, lambda) {
  return (Math.pow(lambda, k) * Math.exp(-lambda)) / factorial(k);
}

function factorial(n) {
  if (n === 0) return 1;
  let result = 1;
  for (let i = 1; i <= n; i++) result *= i;
  return result;
}

function normal(k, mean) {
  return cdf(k + .5, mean, Math.sqrt(mean)) - cdf(k - .5, mean, Math.sqrt(mean))
}


function NormalPoissonGraph({ initialMean = 1 }) {
  const [mean, setMean] = useState(initialMean);
  const [distribution, setDistribution] = useState("poisson");
  const [tailProb, setTailProb] = useState([0,0]);
  const svgRef = useRef();

  const handleMeanChange = (e) => {
    const value = parseInt(e.target.value, 10);
    if (!isNaN(value)) {
      setMean(value);
    }
    };

  const toggleDistribution = () => {
    setDistribution((prev) => (prev === "normal" ? "poisson" : "normal"));
    setMean(mean);
  };



  useEffect(() => {
    const width = 700;
    const height = 400;

    const svg = d3.select(svgRef.current);
    svg.selectAll("*").remove(); // clear previous render

    const data = d3.range(0, 15).map(k => ({
      k,
      p: distribution === "poisson" ? poisson(k, mean) : normal(k, mean, mean)
    }));

    let poissonTail = 0;
    let poissonInd = 14;
    for (let i = 14;i >= 0;i--) {
      let curr = poisson(i, mean);
      if (curr > 0.0005) {
        poissonTail = curr;
        poissonInd = i;
        break;
      }
    }

    let normalTail = normal(poissonInd, mean, mean);

    setTailProb([poissonTail, normalTail]);

    const x = d3
      .scaleBand()
      .domain(data.map(d => d.k))
      .range([40, width - 20])
      .padding(0.1);

    const y = d3
      .scaleLinear()
      .domain([0, .40])
      .nice()
      .range([height - 30, 20]);

    svg
      .attr("width", width)
      .attr("height", height);

    // blue arrow rectangle
    svg.append("rect")
      .attr("x", x(poissonInd) + x.bandwidth() * .35)
      .attr("y", 300)
      .attr("width", x.bandwidth() * .3)
      .attr("height", height - 350)
      .attr("fill", "blue")
      .attr("opacity", 0.7);
    
    // blue arrow arrowhead
    const rectX = x(poissonInd) + x.bandwidth() * 0.35;
    const rectWidth = x.bandwidth() * 0.3;

    // Triangle slightly wider than rectangle
    const triWidth = rectWidth * 1.8;
    const triHeight = 12; // adjust as needed
    const centerX = rectX + rectWidth / 2;
    const baseY = height - 50; // align with x-axis

    svg.append("polygon")
      .attr("points", `
        ${centerX - triWidth / 2},${baseY}
        ${centerX + triWidth / 2},${baseY}
        ${centerX},${baseY + triHeight}
      `)
      .attr("fill", "blue")
      .attr("opacity", 0.7);

    // data bars
    svg
      .selectAll(".bar")
      .data(data)
      .enter()
      .append("rect")
      .attr("class", "bar")
      .attr("x", d => x(d.k))
      .attr("y", d => y(d.p))
      .attr("width", x.bandwidth())
      .attr("height", d => height - 30 - y(d.p));

    svg
      .append("g")
      .attr("transform", `translate(0,${height - 30})`)
      .call(d3.axisBottom(x));

    svg
      .append("g")
      .attr("transform", `translate(40,0)`)
      .call(d3.axisLeft(y));

  }, [mean, distribution]);

  return (
    <div>
      <label>
        Mean:&nbsp;
        <input
          type="number"
          value={mean}
          onChange={handleMeanChange}
          step="1"
          min={1}
          max={5}
        />
      </label>

      <p>
        Current distribution: <strong>{distribution.charAt(0).toUpperCase() + distribution.slice(1)}</strong>
      </p>
    
    <div className="mb-4">
        <button
          onClick={toggleDistribution}
          className="px-4 py-2 bg-gray-800 text-white rounded hover:bg-gray-700"
        >
          Switch Distributions
        </button>
      </div>
    <svg ref={svgRef}></svg>
    <div className="grid grid-cols-2 gap-4">
      <div className="p-8 shadow">
        <span className="text-lg">Poisson tail probability: </span>
        <br></br>
        <span className="text-4xl">{(tailProb[0]*100).toFixed(2)}%</span>
      </div>
      <div className="p-8 shadow">
        <span className="text-lg">Normal tail probability: </span>
        <br></br>
        <span className="text-4xl">{(tailProb[1]*100).toFixed(2)}%</span>
      </div>
    </div>
    </div>
    
  );
}

export default NormalPoissonGraph;
